import type { CryptoSellFilterId } from "../constants/cryptoSwaps.constants";
import { CryptoSellService } from "../services/cryptoSell.service";
import type { OtcSellOrder, OtcSellPair } from "../types/cryptoSell.types";
import { isLiveSale } from "../utils/cryptoSellStatus";
import { buildSellOrdersQuery, DEFAULT_CRYPTO_SELL_FILTERS } from "../utils/cryptoSwapFilters";
import { createOtcPairsCatalog, type UseOtcPairsResult } from "./otcPairsCatalog";
import { useOtcOrderPoller, type UseOtcOrderResult } from "./useOtcOrderPoller";
import { useOtcOrdersPage, type UseOtcOrdersPageResult } from "./useOtcOrdersPage";
import { useSelectedOtcOrder, type UseSelectedOtcOrderResult } from "./useSelectedOtcOrder";

const catalog = createOtcPairsCatalog<OtcSellPair>((organizationId) =>
  CryptoSellService.listPairs(organizationId),
);

const fetchSale = (saleId: string) => CryptoSellService.getOrder(saleId);
const isLiveRow = (sale: OtcSellOrder) => isLiveSale(sale.status);

/** A key created from the sell form changes what the catalog answers. */
export const invalidateCryptoSellPairs = catalog.invalidate;

/** The sale catalog: availability, limits narrowed to the key, and the key the COP are paid to. */
export function useCryptoSellPairs(organizationId: string | null): UseOtcPairsResult<OtcSellPair> {
  return catalog.usePairs(organizationId);
}

/** One sale, polled while the desk or the collection can still move it. */
export function useCryptoSellOrder(saleId: string | null): UseOtcOrderResult<OtcSellOrder> {
  return useOtcOrderPoller(saleId, fetchSale, isLiveRow);
}

export function useCryptoSellOrders(): UseOtcOrdersPageResult<OtcSellOrder, CryptoSellFilterId> {
  return useOtcOrdersPage({
    list: CryptoSellService.listOrders,
    buildQuery: buildSellOrdersQuery,
    isLive: isLiveRow,
    defaultFilters: DEFAULT_CRYPTO_SELL_FILTERS,
  });
}

/** The sale the history's `?sale=` points at. */
export function useSelectedCryptoSell(
  saleId: string | null,
  rows: OtcSellOrder[],
): UseSelectedOtcOrderResult<OtcSellOrder> {
  return useSelectedOtcOrder(saleId, rows, fetchSale);
}
